import { defineStore } from "pinia";
import { AirlineOrderGroup } from "./entity";
import { AirlineOrderGroupList } from "./list.gen";
import type {
  AirlineOrderGroupStore,
  AirlineOrderGroupStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const airlineOrderGroupList = new AirlineOrderGroupList();
export const useAirlineOrderGroupStore = defineStore("airlineOrderGroupStore", {
  state: (): AirlineOrderGroupStoreState => ({
    activeID: null,
    cache: {},
    list: [] as AirlineOrderGroup[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-AirlineOrderGroup", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.AirlineOrderGroup"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(airlineOrderGroup: AirlineOrderGroup): Promise<void> {
      // Set active
      await airlineOrderGroup
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = airlineOrderGroup.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(airlineOrderGroup: AirlineOrderGroup): void {
      const id = airlineOrderGroup.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(airlineOrderGroup);

        // Set active
        if (this.active === null) {
          this.activeID = airlineOrderGroup.id;
        }
      } else {
        this.reinsert(airlineOrderGroup);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as AirlineOrderGroup[]).map((x: any) => {
        const airlineOrderGroup = new AirlineOrderGroup({});
        airlineOrderGroup.set(x as JSONData);
        return airlineOrderGroup;
      });
    },
    async delete(airlineOrderGroup: AirlineOrderGroup): Promise<void> {
      await airlineOrderGroup
        .delete()
        .then(() => {
          this.remove(airlineOrderGroup);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const airlineOrderGroup = (this.list as AirlineOrderGroup[]).find(
        (x: AirlineOrderGroup) => x.id === id,
      );
      if (airlineOrderGroup !== undefined) {
        await this.delete(airlineOrderGroup);
      } else {
        throw new Error("Could not find AirlineOrderGroup with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as AirlineOrderGroup[]).some(
        (x: AirlineOrderGroup) => x.id === id,
      );
    },
    get(id: string): AirlineOrderGroup | null {
      return (
        (this.list as AirlineOrderGroup[]).find(
          (x: AirlineOrderGroup) => x.id === id,
        ) || null
      );
    },
    reinsert(airlineOrderGroup: AirlineOrderGroup): void {
      const index = (this.list as AirlineOrderGroup[]).findIndex(
        (x: AirlineOrderGroup) => x.id === airlineOrderGroup.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          airlineOrderGroup,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(airlineOrderGroup);
      }
      this.updateKey += 1;
    },
    remove({ id }: AirlineOrderGroup): void {
      const index = (this.list as AirlineOrderGroup[]).findIndex(
        (x: AirlineOrderGroup) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<AirlineOrderGroup> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const airlineOrderGroup = this.get(id);
        if (airlineOrderGroup !== null) {
          return airlineOrderGroup;
        }
      }

      // Retrieve airlineOrderGroup
      const airlineOrderGroup = new AirlineOrderGroup({});

      // Get airlineOrderGroup
      await airlineOrderGroup.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate airlineOrderGroup
        await this.activate(entity);
      });

      return airlineOrderGroup;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: AirlineOrderGroup[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as AirlineOrderGroup[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await airlineOrderGroupList.getAll().then(async (list) => {
        list.forEach((airlineOrderGroup) => {
          this.reinsert(airlineOrderGroup);
        });

        // Activate airlineOrderGroup
        if (list.length > 0) {
          if (airlineOrderGroupList.active !== null) {
            this.activeID = airlineOrderGroupList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("airline_order_group_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as AirlineOrderGroup[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineOrderGroup[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as AirlineOrderGroup,
          ),
        };
      }

      // Retrieve list
      const items = await airlineOrderGroupList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineOrderGroup) => {
            this.reinsert(airlineOrderGroup);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineOrderGroup) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineOrderGroup[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as AirlineOrderGroup,
          ),
        };
      }

      // Retrieve list
      const items = await airlineOrderGroupList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineOrderGroup) => {
            this.reinsert(airlineOrderGroup);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineOrderGroup) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineOrderGroup[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map(
            (id: string) => this.get(id) as AirlineOrderGroup,
          ),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await airlineOrderGroupList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineOrderGroup) => {
            this.reinsert(airlineOrderGroup);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineOrderGroup) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(airlineOrderGroup: AirlineOrderGroup): Promise<string> {
      // Update airlineOrderGroup
      await airlineOrderGroup.update();

      // Reinsert airlineOrderGroup
      this.reinsert(airlineOrderGroup);

      // Activate airlineOrderGroup
      if (this.active === null) {
        await this.activate(airlineOrderGroup);
      }

      this.emitStoreUpdateEvent();

      return airlineOrderGroup.id;
    },
  },
  getters: {
    active(): AirlineOrderGroup | null {
      if (this.activeID === null) {
        return null;
      }

      const airlineOrderGroup = (this.list as AirlineOrderGroup[]).find(
        (x: AirlineOrderGroup) => x.id === this.activeID,
      );
      if (airlineOrderGroup === undefined) {
        return null;
      }

      return airlineOrderGroup;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createAirlineOrderGroupStore(): AirlineOrderGroupStore {
  const store = useAirlineOrderGroupStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AirlineOrderGroupStore; // TODO: Fix this
}
